/**
 * №13.4.4/5/6 — matter-scoped trust slice (D-TRU1/2/3). Supersedes blueprint §4.1
 * (the Trust tab no longer launches into the firm-wide Trust place). Everything
 * here stays in MATTER context:
 *   • the surface shows ONLY this matter's trust account statement (running
 *     balance + held/uncleared), read live via get_ledger_account (G2/L-2);
 *   • the action strip is the four trust verbs in plain language — Money in ·
 *     Money out · Transfer between matters · Pay costs from trust (13.4.4). The
 *     word "disbursement" appears nowhere (it is the commercial entity, №12);
 *   • each action opens a form PREFILLED + LOCKED to this matter's trust account
 *     (№11.1 mechanism) — the account can't be chosen wrong;
 *   • Transfer between matters is ONE form (from = this matter, locked → to =
 *     another matter → amount + reason) on the existing journal draft→authorise→
 *     post path (I1-preserving, I9 v2), never two entries the user must correlate.
 *
 * Ledger verb names / statutory vocabulary underneath are UNCHANGED
 * (draft_ledger_receipt / draft_ledger_payment / draft_ledger_journal). Posting
 * stays gated: every action drafts + requests authorisation — a signatory
 * approves it from Needs You. Net on-screen text does not increase.
 */
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useComposeAndRequest, useLedgerAccount, type ComposeInput } from "../../hooks/use-ledger-workbenches";
import { useMatterTrust, useMatters, type MatterTrust } from "../../hooks/use-time-billing";
import { money } from "../../components/ledger/format";

type TrustAction = "receipt" | "payment" | "transfer" | "costs";

// Chrome labels ONLY (13.4.4). The verb each maps to is fixed underneath.
const ACTIONS: { key: TrustAction; label: string }[] = [
  { key: "receipt", label: "Money in" },
  { key: "payment", label: "Money out" },
  { key: "transfer", label: "Transfer between matters" },
  { key: "costs", label: "Pay costs from trust" },
];

const today = () => new Date().toISOString().slice(0, 10);
function toMinor(s: string): number {
  const n = Math.round(parseFloat(s.replace(/[^0-9.-]/g, "")) * 100);
  return Number.isFinite(n) ? n : 0;
}

interface StatementEntry {
  direction: string; amount_minor: number; running_balance_minor: number | null;
  txn_no: number | null; type: string; doc_no: string | null; effective_date: string;
  counterparty_name: string | null; reason: string | null; txn_status: string;
}

type MatterAccount = MatterTrust["accounts"][number];

export function MatterTrustSlice({ matterId, trust, onRefetch }: {
  matterId: string;
  trust: MatterTrust;
  onRefetch: () => void;
}) {
  const account = trust.accounts[0];
  const stmt = useLedgerAccount(account?.account_id);
  const [action, setAction] = useState<TrustAction | null>(null);
  const entries = (stmt.data?.entries ?? []) as unknown as StatementEntry[];

  if (!account) return null;

  return (
    <>
      {/* this matter's trust position (never stored — G2/L-2) */}
      <div className="threeb" style={{ marginBottom: 12 }}>
        <div className="bal bal--trust"><span className="k">Held</span><span className="v money">{money(trust.balance_minor)}</span><span className="src">{account.account_no} · {account.title_name}</span></div>
        <div className="bal"><span className="k">Available</span><span className="v money">{money(trust.available_minor)}</span><span className="src">cleared</span></div>
        <div className="bal"><span className="k">Uncleared</span><span className="v money">{money(trust.uncleared_minor)}</span></div>
      </div>

      {/* the four trust actions (13.4.4) — matter-scoped, account locked */}
      <div className="actionbar">
        {ACTIONS.map((a) => (
          <button key={a.key} className="act" onClick={() => setAction(a.key)}>{a.label}</button>
        ))}
      </div>
      <div className="sub-note">Recorded against <b>this matter’s trust account</b> ({account.account_no}) — the account is locked to the matter; posting stays gated (a signatory approves from Needs You).</div>

      {/* the matter's own statement (running balance) — no firm-wide navigation */}
      <div style={{ marginTop: 14 }}>
        <div className="section-head"><h3 style={{ fontSize: 13 }}>Statement · {account.account_no}</h3><span className="count">this matter · running balance</span></div>
        {stmt.isLoading ? (
          <div className="muted tiny">Loading statement…</div>
        ) : entries.length === 0 ? (
          <div className="muted tiny">No trust transactions on this matter yet.</div>
        ) : (
          <table className="ledger-table">
            <thead><tr><th>Date</th><th>#</th><th>Particulars</th><th className="num">Debit</th><th className="num">Credit</th><th className="num">Balance</th></tr></thead>
            <tbody>
              {entries.map((en, i) => (
                <tr key={i}>
                  <td className="row-date">{en.effective_date}</td>
                  <td className="row-doc">{en.doc_no ?? (en.txn_no ? String(en.txn_no) : "—")}</td>
                  <td>{en.reason ?? en.counterparty_name ?? en.type}</td>
                  <td className="num money">{en.direction === "dr" ? money(en.amount_minor) : ""}</td>
                  <td className="num money">{en.direction === "cr" ? money(en.amount_minor) : ""}</td>
                  <td className={`num money${(en.running_balance_minor ?? 0) < 0 ? " money--neg" : ""}`}>{money(en.running_balance_minor)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {action && (
        <TrustActionModal
          action={action}
          matterId={matterId}
          account={account}
          onClose={() => setAction(null)}
          onDone={() => { void stmt.refetch(); onRefetch(); }}
        />
      )}
    </>
  );
}

function TrustActionModal({ action, matterId, account, onClose, onDone }: {
  action: TrustAction;
  matterId: string;
  account: MatterAccount;
  onClose: () => void;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const compose = useComposeAndRequest();
  const matters = useMatters();
  const [effectiveDate, setEffectiveDate] = useState(today());
  const [reason, setReason] = useState("");
  const [amount, setAmount] = useState("");
  const [party, setParty] = useState("");
  const [method, setMethod] = useState("eft");
  const [instrument, setInstrument] = useState("eft");
  const [invoiceRef, setInvoiceRef] = useState("");
  const [toMatter, setToMatter] = useState("");

  // Transfer destination: resolve the chosen matter's trust account in the SAME
  // ledger (a journal moves value within one ledger). If it has none, block with
  // a teaching message rather than silently failing.
  const destTrust = useMatterTrust(action === "transfer" && toMatter ? toMatter : undefined);
  const destAccount = destTrust.data?.accounts?.find((a) => a.ledger_id === account.ledger_id);

  const title = action === "receipt" ? "Money in"
    : action === "payment" ? "Money out"
    : action === "transfer" ? "Transfer between matters"
    : "Pay costs from trust";

  const amountMinor = toMinor(amount);
  const valid = (() => {
    if (amountMinor <= 0) return false;
    if (action === "transfer") return !!toMatter && toMatter !== matterId && !!destAccount && !!reason.trim();
    if (action === "costs") return !!invoiceRef.trim();
    return !!reason.trim();
  })();

  const otherMatters = (matters.data ?? []).filter((m) => m.id !== matterId);

  function submit() {
    const type: ComposeInput["type"] = action === "receipt" ? "receipt" : action === "transfer" ? "journal" : "payment";
    const input: ComposeInput = { type, ledgerId: account.ledger_id, effective_date: effectiveDate, reason: reason.trim() };
    if (action === "receipt") {
      input.method = method;
      input.counterparty_name = party || undefined;
      input.lines = [{ account_id: account.account_id, amount_minor: amountMinor }];
    } else if (action === "payment") {
      input.payee = party || undefined;
      input.instrument_kind = instrument;
      input.authorised_by = "practitioner";
      input.lines = [{ account_id: account.account_id, amount_minor: amountMinor }];
    } else if (action === "costs") {
      // Pay costs from trust — the invoice-referencing money-out (D-TRU1). The
      // invoice/bill reference carries in the statutory reason (draft_ledger_payment
      // takes no external ref; reason is the compulsory statutory purpose).
      input.payee = party || "Office account";
      input.instrument_kind = instrument;
      input.authorised_by = "practitioner";
      input.reason = `${reason.trim() ? `${reason.trim()} — ` : "Legal costs — "}invoice ${invoiceRef.trim()}`;
      input.lines = [{ account_id: account.account_id, amount_minor: amountMinor }];
    } else {
      // Transfer between matters — ONE journal, from this matter → the other.
      input.from_account_id = account.account_id;
      input.to_account_id = destAccount!.account_id;
      input.amount_minor = amountMinor;
      input.authorised_by = "practitioner";
    }
    compose.mutate(input, {
      onSuccess: () => {
        // Completion contract (№13.3): refresh both legs' balances + statements.
        void qc.invalidateQueries({ queryKey: ["legal", "trust-balance"] });
        void qc.invalidateQueries({ queryKey: ["ledger", "account"] });
        onDone();
        onClose();
      },
    });
  }

  const overlay: React.CSSProperties = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, fontFamily: "var(--font-sans)" };
  const box: React.CSSProperties = { background: "var(--color-surface-1, #fff)", borderRadius: 12, padding: 20, width: "min(520px, 92vw)", maxHeight: "88vh", overflow: "auto", boxShadow: "0 12px 48px rgba(0,0,0,0.25)" };

  return (
    <div style={overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div role="dialog" aria-modal="true" style={box} className="vt">
        <div className="row" style={{ marginBottom: 12 }}>
          <strong style={{ fontSize: 15 }}>{title}</strong>
          <span className="spacer" />
          <span className="pill pill--draft"><span className="dot" />draft</span>
        </div>

        {/* the locked account — this matter's trust account (№11.1 prefill+lock) */}
        <div className="field">
          <label>Trust account</label>
          <div className="lock-chip" style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 10px", borderRadius: 8, background: "var(--color-surface-2, #f4f4f5)", fontSize: 13 }}>
            <i className="ti ti-lock" aria-hidden="true" /> {account.account_no} · {account.title_name}
          </div>
        </div>

        <div className="field-row">
          <div className="field"><label>Effective date</label><input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} /></div>
          {action === "receipt" ? (
            <div className="field"><label>Method</label><select value={method} onChange={(e) => setMethod(e.target.value)}><option value="eft">EFT</option><option value="cheque">Cheque</option><option value="cash">Cash</option><option value="direct_deposit">Direct deposit</option></select></div>
          ) : action !== "transfer" ? (
            <div className="field"><label>Instrument</label><select value={instrument} onChange={(e) => setInstrument(e.target.value)}><option value="eft">EFT</option><option value="cheque">Cheque</option><option value="bpay">BPay</option></select></div>
          ) : null}
        </div>

        {action === "receipt" && (
          <div className="field"><label>Received from</label><input type="text" value={party} onChange={(e) => setParty(e.target.value)} placeholder="Drawer" /></div>
        )}
        {action === "payment" && (
          <div className="field"><label>Paid to</label><input type="text" value={party} onChange={(e) => setParty(e.target.value)} placeholder="Payee" /></div>
        )}
        {action === "costs" && (
          <>
            <div className="field"><label>Paid to</label><input type="text" value={party} onChange={(e) => setParty(e.target.value)} placeholder="Office account" /></div>
            <div className="field"><label>Invoice / bill reference</label><input type="text" value={invoiceRef} onChange={(e) => setInvoiceRef(e.target.value)} placeholder="e.g. INV-0001 (compulsory)" /></div>
          </>
        )}
        {action === "transfer" && (
          <div className="field">
            <label>To matter</label>
            <select value={toMatter} onChange={(e) => setToMatter(e.target.value)}>
              <option value="">Choose destination matter…</option>
              {otherMatters.map((m) => <option key={m.id} value={m.id as string}>{m.id} · {(m.title as string) ?? ""}</option>)}
            </select>
            {toMatter && !destTrust.isLoading && !destAccount && (
              <div className="muted tiny" style={{ marginTop: 4, color: "var(--color-danger, #b91c1c)" }}>That matter has no trust account in this ledger — open its Trust tab and add one first.</div>
            )}
          </div>
        )}

        <div className="field"><label>Amount</label><input type="text" className="tabular" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" /></div>
        <div className="field"><label>Reason / purpose{action === "costs" ? " (optional)" : ""}</label><input type="text" value={reason} onChange={(e) => setReason(e.target.value)} placeholder={action === "costs" ? "Defaults to “Legal costs”" : "Compulsory (statutory)"} /></div>

        {compose.isError ? (
          <div className="stale-banner" style={{ marginTop: 10 }}><svg className="icon" viewBox="0 0 24 24"><path d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></svg><div>{(compose.error as Error).message}</div></div>
        ) : null}

        <div className="row" style={{ marginTop: 16 }}>
          <button onClick={onClose}>Cancel</button>
          <span className="spacer" />
          <button data-variant="primary" disabled={!valid || compose.isPending} onClick={submit}>{compose.isPending ? "Requesting…" : "Request authorisation to post"}</button>
        </div>
        <span className="muted tiny" style={{ display: "block", marginTop: 8 }}>Posting is gated: a signatory approves it from Needs You. Every draft is working paper until authorised.</span>
      </div>
    </div>
  );
}
